import React from 'react';

import { Empty, Tag, Timeline, Typography } from 'antd';

import {
  AnswerKeyRating,
  ConvictionCoverageNote,
  ParticipationNote,
  readAnswerKeyGate,
} from 'components';

import { TimelineItem } from './api';
import { SOLO_STATUS_DOT_COLORS, formatScore, formatWhen } from './helpers';
import SoloOutcome from './SoloOutcome';

/**
 * The score of a finished assessment session, with the ANSWER KEY's own
 * verdict standing beside it.
 *
 * A score is the distance between a candidate's calls and a reviewed answer
 * key, and a key that failed its quality audit may show its numbers but may
 * not wear a hiring band: the failing check is precisely the finding that a
 * candidate who expresses no view can pass. The band therefore goes through
 * `AnswerKeyRating`, the one component that owns that rule, so this screen
 * cannot drift from the trainer's report and the admin results screen — which
 * is exactly what happened when the rule shipped on those two alone.
 *
 * The numbers themselves stay. Unproven is not the same as wrong, and a LOW
 * score against a weak key is still real information.
 */
const AssessmentLine: React.FC<{
  assessment: NonNullable<TimelineItem['assessment']>;
}> = ({ assessment }) => {
  const gate = readAnswerKeyGate(assessment);
  return (
    <>
      <div>
        Assessment:{' '}
        <AnswerKeyRating
          rating={assessment.rating}
          gate={gate}
          showDiagnostics={false}
        />{' '}
        · {formatScore(assessment.official_composite)}
        {assessment.rank ? ` · #${assessment.rank}` : ''}
      </div>
      {/* The same outcome facts as the roster cell — see RosterTable. */}
      <div>
        <ParticipationNote participation={assessment.participation} />
      </div>
      <div>
        <ConvictionCoverageNote coverage={assessment.conviction_coverage} />
      </div>
    </>
  );
};

export const TIMELINE_LIMIT = 100;

/** Plain-text title of a timeline item — mirrors the bold heading each entry
 *  renders; used by the Studio inspect card. */
export const timelineItemTitle = (item: TimelineItem): string => {
  const rank = item.rank ? ` (rank ${item.rank})` : '';
  const sessionTitle = item.blueprint
    ? `Assessment session: ${item.blueprint.name}`
    : `Assessment session #${item.session_id}`;
  switch (item.type) {
    case 'session_started':
      return sessionTitle;
    case 'session_finished':
      return sessionTitle;
    case 'solo_session':
      return `${item.node_name} → ${item.mini_session_name}`;
    case 'attempt_scenario':
      return `Node: ${item.node_name || item.asset_title || '—'}${rank}`;
    case 'attempt_ladder':
      return `Warm-up: ${item.node_name || item.asset_title || '—'}${rank}`;
    case 'attempt_game':
      return `Mini game: ${item.asset_title || item.asset_key || '—'}`;
    case 'node_unlocked':
      return `Unlocked node: ${item.node_name}`;
    default:
      return item.type;
  }
};

const TrackPill: React.FC<{ item: TimelineItem }> = ({ item }) =>
  item.node_track ? (
    <Tag
      color={item.node_track_color || undefined}
      style={{ marginLeft: 8, verticalAlign: 'middle' }}
    >
      {item.node_track} track
    </Tag>
  ) : null;

const timelineEntry = (item: TimelineItem) => {
  switch (item.type) {
    case 'session_started':
      return {
        color: 'blue',
        content: (
          <>
            <strong>{timelineItemTitle(item)}</strong>
            <div>Started playing</div>
          </>
        ),
      };
    case 'session_finished':
      return {
        color: item.played ? 'green' : 'gray',
        content: (
          <>
            <strong>{timelineItemTitle(item)}</strong>
            <div>
              Finished ·{' '}
              {item.played ? `${item.deals} deals` : 'No participation'}
            </div>
            {item.assessment && <AssessmentLine assessment={item.assessment} />}
          </>
        ),
      };
    case 'solo_session':
      return {
        color: SOLO_STATUS_DOT_COLORS[item.status ?? ''] || 'blue',
        content: (
          <>
            <strong>{timelineItemTitle(item)}</strong>
            <TrackPill item={item} />
            <SoloOutcome
              item={{
                status: item.status,
                score_pct: item.score_pct ?? null,
                pass_bar: item.pass_bar ?? null,
                conviction_coverage: item.conviction_coverage,
              }}
            />
          </>
        ),
      };
    case 'attempt_scenario':
      return {
        color: item.passed ? 'green' : 'red',
        content: (
          <>
            <strong>
              Node: {item.node_name || item.asset_title || '—'}
              {item.rank ? ` (rank ${item.rank})` : ''}
            </strong>
            <TrackPill item={item} />
            <div>
              {item.passed ? 'Passed' : 'Did not pass'} ·{' '}
              {formatScore(item.score_pct)}
            </div>
          </>
        ),
      };
    case 'attempt_ladder':
      return {
        color: item.passed ? 'green' : 'orange',
        content: (
          <>
            <strong>
              Warm-up: {item.node_name || item.asset_title || '—'}
              {item.rank ? ` (rank ${item.rank})` : ''} —{' '}
              {item.passed ? 'done' : 'not passed'}
            </strong>
            <TrackPill item={item} />
          </>
        ),
      };
    case 'attempt_game':
      return {
        color: 'blue',
        content: (
          <strong>
            Mini game: {item.asset_title || item.asset_key || '—'} ·{' '}
            {formatScore(item.score_pct)}
          </strong>
        ),
      };
    case 'node_unlocked':
      return {
        color: 'purple',
        content: <strong>Unlocked node: {item.node_name}</strong>,
      };
    default:
      return { color: 'gray', content: <span>{item.type}</span> };
  }
};

type Props = {
  timeline: TimelineItem[];
  /** When provided, items become clickable (Studio inspect flow). */
  onSelectItem?: (item: TimelineItem) => void;
  selectedItem?: TimelineItem | null;
};

// Item types with no inspect view: plain rows — no cursor, no hover, no click.
const NON_INSPECTABLE_TYPES = new Set(['session_started']);

/** Whether clicking this item opens an inspect view (also used by the Studio
 *  to auto-select the newest inspectable item on load). */
export const isInspectableItem = (item: TimelineItem) =>
  !NON_INSPECTABLE_TYPES.has(item.type);

/** The trainee's unified activity feed, shared by the drawer and the studio
 *  workspace. Renders the latest TIMELINE_LIMIT items of the given feed. */
const TraineeTimeline: React.FC<Props> = ({
  timeline,
  onSelectItem,
  selectedItem,
}) => {
  const items = timeline.slice(0, TIMELINE_LIMIT);
  if (items.length === 0) return <Empty description="No recorded activity" />;
  return (
    <Timeline
      items={items.map(item => {
        const entry = timelineEntry(item);
        const clickable = onSelectItem !== undefined && isInspectableItem(item);
        return {
          color: entry.color,
          children: (
            <div
              className={
                clickable
                  ? `cstudio__tl-item ${
                      selectedItem === item ? 'cstudio__tl-item--active' : ''
                    }`
                  : undefined
              }
              onClick={clickable ? () => onSelectItem(item) : undefined}
            >
              {entry.content}
              <div>
                <Typography.Text type="secondary">
                  {formatWhen(item.timestamp)}
                </Typography.Text>
              </div>
            </div>
          ),
        };
      })}
    />
  );
};

export default TraineeTimeline;
